import { useState, useEffect } from 'react';
import { Plus, X, Info, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  BillingCycle, 
  ProductBillingCycles, 
  DEFAULT_BILLING_CYCLES 
} from '@/types/billing';
import {
  useBillingCycles,
  useCreateBillingCycle,
  useUpdateBillingCycle,
  useDeleteBillingCycle,
  useShortestBillingCycle
} from '@/hooks/billing';
import { toast } from 'sonner';

interface BillingCyclesConfigurationProps {
  productId: string;
  onSave?: (_config: ProductBillingCycles) => void;
}

export function BillingCyclesConfiguration({ productId, onSave }: BillingCyclesConfigurationProps) {
  const { t } = useTranslation();
  
  // API hooks
  const { 
    data: billingCycles = [], 
    isLoading, 
    error 
  } = useBillingCycles(productId);
  
  const createCycleMutation = useCreateBillingCycle();
  const updateCycleMutation = useUpdateBillingCycle();
  const deleteCycleMutation = useDeleteBillingCycle();
  const { data: shortestCycle } = useShortestBillingCycle(productId);
  
  // Local state for UI
  const [availableCycles, setAvailableCycles] = useState<BillingCycle[]>([]);
  const [showAddCustom, setShowAddCustom] = useState(false);
  const [customCycleName, setCustomCycleName] = useState('');
  const [customCycleMonths, setCustomCycleMonths] = useState('');
  
  // Sync API data with local state
  useEffect(() => {
    if (billingCycles.length > 0) {
      // Map API cycles to component format
      const mappedCycles = billingCycles.map(cycle => {
        const computedDisplayName = cycle.display_name || cycle.displayName || cycle.name || `${cycle.months} month${cycle.months > 1 ? 's' : ''}`;
        return {
          ...cycle,
          display_name: computedDisplayName,
          displayName: computedDisplayName,
          id: cycle.id || `cycle-${cycle.months}`,
          type: (cycle.type as BillingCycle['type']) || 'standard'
        };
      });
      // Only update if actually different to prevent infinite loops
      setAvailableCycles(prev => {
        const hasChanged = prev.length !== mappedCycles.length ||
          prev.some((p, i) => p.id !== mappedCycles[i]?.id);
        return hasChanged ? (mappedCycles as BillingCycle[]) : prev;
      });
    } else if (!isLoading) {
      // Default cycles if none exist (only set when not loading)
      setAvailableCycles(prev => {
        if (prev.length === 0) {
          return [
            { ...DEFAULT_BILLING_CYCLES[0], isDefault: true }, // Monthly
            { ...DEFAULT_BILLING_CYCLES[3], isDefault: true }, // Annual
          ];
        }
        return prev;
      });
    }
  }, [billingCycles, isLoading]);
  
  // Handle API errors
  useEffect(() => {
    if (error) {
      toast.error(t('errors.failedToLoadBillingCycles'));
    }
  }, [error, t]);

  // Helper function to get translated cycle name
  const getTranslatedCycleName = (cycleId: string): string => {
    const cycleKeyMap: { [key: string]: string } = {
      'monthly': 'monthly',
      'quarterly': 'quarterly',
      'semi-annual': 'semiAnnual',
      'annual': 'yearly',  // Maps to 'yearly' in translations
      'biennial': 'biennial'
    };
    
    const translationKey = cycleKeyMap[cycleId];
    return translationKey ? t(`products.${translationKey}`) : '';
  };

  // Helper function to get translated display name
  const getTranslatedDisplayName = (cycle: BillingCycle): string => {
    if (cycle.id === 'monthly') return t('products.monthly');
    if (cycle.id === 'annual' || cycle.id === 'yearly') return t('products.yearly');
    return t('products.everyXMonths', { count: cycle.months });
  };

  const handleToggleCycle = async (cycleId: string) => {
    // Find the existing API cycle that matches the default cycle by months
    const defaultCycle = DEFAULT_BILLING_CYCLES.find(c => c.id === cycleId);
    const existingCycle = availableCycles.find(c => c.months === defaultCycle?.months && c.type === 'standard');
    
    try {
      if (existingCycle) {
        // Remove existing cycle using its actual API ID
        await deleteCycleMutation.mutateAsync({
          productId,
          cycleId: existingCycle.id!
        });
        toast.success(t('products.billingCycleRemoved'));
      } else {
        // Add from defaults
        const defaultCycle = DEFAULT_BILLING_CYCLES.find(c => c.id === cycleId);
        if (defaultCycle) {
          await createCycleMutation.mutateAsync({
            productId,
            data: {
              type: 'standard',
              name: defaultCycle.name,
              months: defaultCycle.months,
              interval: defaultCycle.months === 1 ? 'month' : 
                        defaultCycle.months === 12 ? 'year' : 'month',
              interval_count: 1,
              display_name: defaultCycle.displayName,
              is_active: true
            }
          });
          toast.success(t('products.billingCycleAdded'));
        }
      }
      } catch (_error) {
        toast.error(t('errors.failedToUpdateBillingCycle'));
      }
    };


  const handleAddCustomCycle = async () => {
    const months = parseInt(customCycleMonths);
    if (!customCycleName || !months || months < 1) return;

    try {
      await createCycleMutation.mutateAsync({
        productId,
        data: {
          type: 'custom',
          name: customCycleName,
          months: months,
          interval: 'month',
          interval_count: months,
          display_name: t('products.everyXMonths', { count: months }),
          is_active: true
        }
      });
      
      toast.success(t('products.customBillingCycleCreated'));
      setShowAddCustom(false);
      setCustomCycleName('');
      setCustomCycleMonths('');
      
      // Call onSave callback if provided
      if (onSave) {
        onSave({ 
          productId,
          availableCycles,
          shortestCycleId: availableCycles.length > 0 ? availableCycles[0].id! : ''
        });
      }
    } catch (_error) {
      toast.error(t('errors.failedToCreateBillingCycle'));
    }
  };

  const handleRemoveCustomCycle = async (cycleId: string) => {
    try {
      await deleteCycleMutation.mutateAsync({
        productId,
        cycleId
      });
      toast.success(t('products.customBillingCycleRemoved'));
      
      // Call onSave callback if provided
      if (onSave) {
        const remainingCycles = availableCycles.filter(c => c.id !== cycleId);
        onSave({ 
          productId,
          availableCycles: remainingCycles,
          shortestCycleId: remainingCycles.length > 0 ? remainingCycles[0].id! : ''
        });
      }
    } catch (_error) {
      toast.error(t('errors.failedToRemoveBillingCycle'));
    }
  };

  // Use the shortest cycle from API or calculate locally
  const displayShortestCycle = shortestCycle || (availableCycles.length > 0 
    ? availableCycles.reduce((shortest, current) => 
        current.months < shortest.months ? current : shortest
      )
    : null);
    
  // Loading state
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-64 mb-2" />
          <Skeleton className="h-4 w-96" />
        </CardHeader>
        <CardContent className="space-y-6">
          <Skeleton className="h-16 w-full" />
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('products.billingCyclesConfiguration')}</CardTitle>
        <CardDescription>
          {t('products.manageBillingCyclesDescription')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            {t('products.billingCycleInfo')}
          </AlertDescription>
        </Alert>

        {/* Default Cycles */}
        <div>
          <Label className="text-base mb-3 block">{t('products.standardBillingCycles')}</Label>
          <div className="space-y-3">
            {DEFAULT_BILLING_CYCLES.map((cycle) => {
              const isEnabled = availableCycles.some(c => c.months === cycle.months && c.type === 'standard');
              const enabledCycle = availableCycles.find(c => c.months === cycle.months && c.type === 'standard');
              const isShortestCycle = enabledCycle && displayShortestCycle && enabledCycle.months === displayShortestCycle.months;
              const isUpdating = (updateCycleMutation.isPending || createCycleMutation.isPending || deleteCycleMutation.isPending);
              
              return (
                <div key={cycle.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id={cycle.id}
                      checked={isEnabled}
                      onCheckedChange={() => handleToggleCycle(cycle.id)}
                      disabled={isUpdating}
                    />
                    {isUpdating && (
                      <Loader2 className="h-4 w-4 animate-spin ml-2" />
                    )}
                    <Label htmlFor={cycle.id} className="cursor-pointer">
                      <span className="font-medium">{getTranslatedCycleName(cycle.id) || cycle.name}</span>
                      <span className="text-sm text-muted-foreground ml-2">
                        ({getTranslatedDisplayName(cycle)})
                      </span>
                    </Label>
                  </div>
                  {isEnabled && isShortestCycle && (
                    <Badge variant="secondary">{t('products.creditResetCycle')}</Badge>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Custom Cycles */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <Label className="text-base">{t('products.customBillingCycles')}</Label>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAddCustom(true)}
            >
              <Plus className="h-4 w-4 mr-1" />
              {t('products.addCustom')}
            </Button>
          </div>
          
          {availableCycles.filter(c => c.type === 'custom').length > 0 ? (
            <div className="space-y-2">
              {availableCycles.filter(c => c.type === 'custom').map((cycle) => (
                <div key={cycle.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <span className="font-medium">{cycle.name}</span>
                    <span className="text-sm text-muted-foreground ml-2">
                      ({getTranslatedDisplayName(cycle)})
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {displayShortestCycle && cycle.months === displayShortestCycle.months && (
                      <Badge variant="secondary">{t('products.creditResetCycle')}</Badge>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveCustomCycle(cycle.id!)}
                      disabled={deleteCycleMutation.isPending}
                    >
                      {deleteCycleMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <X className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{t('products.noCustomCyclesConfigured')}</p>
          )}
        </div>

        {/* Summary */}
        {availableCycles.length > 0 && displayShortestCycle && (
          <Alert>
            <AlertDescription>
              <strong>{t('products.automaticCreditResetCycle')}:</strong> {getTranslatedCycleName(displayShortestCycle.id!) || displayShortestCycle.name} ({getTranslatedDisplayName({...displayShortestCycle, displayName: 'displayName' in displayShortestCycle ? displayShortestCycle.displayName : displayShortestCycle.name} as BillingCycle)})
              <br />
              {t('products.creditsWillResetInfo', { count: displayShortestCycle.months })}
            </AlertDescription>
          </Alert>
        )}

        {/* Add Custom Dialog */}
        <Dialog open={showAddCustom} onOpenChange={setShowAddCustom}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('products.addCustomBillingCycle')}</DialogTitle>
              <DialogDescription>
                {t('products.createCustomBillingCycleDescription')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="cycle-name">{t('products.cycleName')}</Label>
                <Input
                  id="cycle-name"
                  placeholder={t('products.cycleNamePlaceholder')}
                  value={customCycleName}
                  onChange={(e) => setCustomCycleName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cycle-months">{t('products.durationMonths')}</Label>
                <Input
                  id="cycle-months"
                  type="number"
                  min="1"
                  placeholder="e.g., 2"
                  value={customCycleMonths}
                  onChange={(e) => setCustomCycleMonths(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddCustom(false)}>
                {t('common.cancel')}
              </Button>
              <Button 
                onClick={handleAddCustomCycle}
                disabled={!customCycleName || !customCycleMonths || parseInt(customCycleMonths) < 1 || createCycleMutation.isPending}
              >
                {createCycleMutation.isPending && (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                )}
                {t('products.addCycle')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
